'use client';

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { BlockIdentityContext, type MicroContentItem } from '@akademi/site-builder-runtime';
import { MicroContentPopup } from './MicroContentPopup';
import type { ShortsSequencer } from './shortsSequencer';

/**
 * App-level host for the micro player popup. Micro has NO detail route —
 * content plays in {@link MicroContentPopup} wherever the card was
 * clicked (legacy parity). Mount ONE host above the page tree (inside
 * the MicroSource provider); every {@code MicroItemCard} then opens the
 * popup through {@link useMicroPopup} instead of navigating.
 */

export interface MicroPopupOpenOptions {
  /** Next/previous policy for the fullscreen shorts reel. Omitted →
   *  the popup's default engine (the SHORTS kind feed in display
   *  order). Surfaces with their own curation — or a future
   *  interest-based ranker — pass their {@link ShortsSequencer} here. */
  shortsSequencer?: ShortsSequencer;
}

interface MicroPopupApi {
  open(item: MicroContentItem, options?: MicroPopupOpenOptions): void;
}

const MicroPopupContext = createContext<MicroPopupApi | null>(null);

/** Null when no host is mounted — cards fall back to their href. */
export function useMicroPopup(): MicroPopupApi | null {
  return useContext(MicroPopupContext);
}

export function MicroPopupHost({ children }: { children: ReactNode }) {
  const [opened, setOpened] = useState<{
    item: MicroContentItem;
    options?: MicroPopupOpenOptions;
  } | null>(null);
  const open = useCallback(
    (next: MicroContentItem, options?: MicroPopupOpenOptions) => setOpened({ item: next, options }),
    [],
  );
  const api = useMemo<MicroPopupApi>(() => ({ open }), [open]);

  return (
    <MicroPopupContext.Provider value={api}>
      {children}
      {/* The popup is an app-level surface, not a registered block — give it a
          synthetic identity so its dialog + controls still carry
          data-<ns>-block / data-<ns>-el (see MicroContentPopup). */}
      <BlockIdentityContext.Provider value={{ type: 'MicroContentPopup', id: 'MicroContentPopup' }}>
        <MicroContentPopup
          item={opened?.item}
          shortsSequencer={opened?.options?.shortsSequencer}
          onClose={() => setOpened(null)}
        />
      </BlockIdentityContext.Provider>
    </MicroPopupContext.Provider>
  );
}
